import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, X, ImageIcon, Repeat } from 'lucide-react';
import { format } from 'date-fns';
import { createMaintenanceTask, updateMaintenanceTask, updateMaintenanceTaskPhotos } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ShareWithPicker } from '@/components/ShareWithPicker';
import { PhotoUploader } from '@/components/PhotoUploader';

const RECURRENCE_PATTERNS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

export const MaintenanceDialog = ({ 
  open, 
  onClose, 
  selectedDate, 
  properties, 
  selectedProperty,
  defaultCategory = 'general',
  editingTask = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    title: '',
    description: '',
    scheduled_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    priority: 'medium',
    category: defaultCategory,
    estimated_cost: 0,
    assigned_to: '',
    collaborator_ids: [],
    photos: [],
    hourly_rate: '',
    is_recurring: false,
    recurrence_pattern: null,
  });
  const [date, setDate] = useState(selectedDate || null);

  useEffect(() => {
    if (open) {
      if (editingTask) {
        setFormData({
          property_id: editingTask.property_id || selectedProperty?.id || '',
          title: editingTask.title || '',
          description: editingTask.description || '',
          scheduled_date: editingTask.scheduled_date || '',
          priority: editingTask.priority || 'medium',
          category: editingTask.category || defaultCategory,
          estimated_cost: editingTask.estimated_cost || 0,
          assigned_to: editingTask.assigned_to || '',
          collaborator_ids: editingTask.collaborator_ids || [],
          photos: editingTask.photos || [],
          hourly_rate: editingTask.hourly_rate || '',
          is_recurring: editingTask.is_recurring || false,
          recurrence_pattern: editingTask.recurrence_pattern || null,
        });
        if (editingTask.scheduled_date) {
          setDate(new Date(editingTask.scheduled_date + 'T00:00:00'));
        }
      } else {
        if (selectedProperty) {
          setFormData(prev => ({ ...prev, property_id: selectedProperty.id, category: defaultCategory }));
        } else {
          setFormData(prev => ({ ...prev, category: defaultCategory }));
        }
        if (selectedDate) {
          setDate(selectedDate);
          setFormData(prev => ({ ...prev, scheduled_date: format(selectedDate, 'yyyy-MM-dd') }));
        }
      }
    }
  }, [open, selectedProperty, selectedDate, defaultCategory, editingTask]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.title || !formData.scheduled_date) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const taskData = {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
      };
      
      // Only include recurrence_pattern if is_recurring is true
      if (!formData.is_recurring) {
        taskData.recurrence_pattern = null;
      }
      
      if (editingTask) {
        // Update endpoint only accepts a subset of fields
        const updatePayload = {
          title: taskData.title,
          description: taskData.description,
          priority: taskData.priority,
          category: taskData.category,
          scheduled_date: taskData.scheduled_date,
          estimated_cost: taskData.estimated_cost,
          assigned_to: taskData.assigned_to,
          collaborator_ids: taskData.collaborator_ids,
          hourly_rate: taskData.hourly_rate,
        };
        await updateMaintenanceTask(editingTask.id, updatePayload);
        toast.success(taskData.category === 'landscaping'
          ? 'Landscaping task updated'
          : 'Maintenance task updated');
      } else {
        await createMaintenanceTask(taskData);
        const isLandscaping = taskData.category === 'landscaping';
        toast.success(formData.is_recurring 
          ? `Recurring ${isLandscaping ? 'landscaping' : 'maintenance'} task created`
          : `${isLandscaping ? 'Landscaping' : 'Maintenance'} task created successfully`);
      }
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const addPhoto = async (url) => {
    if (!url || !url.trim()) return;
    const clean = url.trim();
    const updated = [...formData.photos, clean];
    setFormData((prev) => ({ ...prev, photos: updated }));
    // If we're editing an existing task, persist immediately.
    if (editingTask?.id) {
      try {
        await updateMaintenanceTaskPhotos(editingTask.id, updated);
      } catch (_) {
        toast.error('Failed to save photo');
        setFormData((prev) => ({
          ...prev,
          photos: prev.photos.filter((p) => p !== clean),
        }));
      }
    }
  };

  const removePhoto = async (index) => {
    const updated = formData.photos.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, photos: updated }));
    if (editingTask?.id) {
      try {
        await updateMaintenanceTaskPhotos(editingTask.id, updated);
      } catch (_) {
        toast.error('Failed to remove photo');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty?.id || '',
      title: '',
      description: '',
      scheduled_date: '',
      priority: 'medium',
      category: defaultCategory,
      estimated_cost: 0,
      assigned_to: '',
      collaborator_ids: [],
      photos: [],
      hourly_rate: '',
      is_recurring: false,
      recurrence_pattern: null,
    });
    setDate(null);
  };

  return (
    <Dialog open={open} onOpenChange={() => { resetForm(); onClose(); }}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="maintenance-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">
            {editingTask ? 'Edit ' : 'New '}
            {formData.category === 'landscaping' ? 'Landscaping' : 'Maintenance'} Task
          </DialogTitle>
          <DialogDescription className="text-slate-500">Log a maintenance task with priority and worker.</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Property Selection */}
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
            >
              <SelectTrigger data-testid="maintenance-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Fix leaky faucet"
              className="bg-slate-50"
              data-testid="maintenance-title-input"
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger data-testid="maintenance-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General Maintenance</SelectItem>
                <SelectItem value="landscaping">Landscaping</SelectItem>
                <SelectItem value="repair">Repair</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label>Priority *</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
            >
              <SelectTrigger data-testid="maintenance-priority-select">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scheduled Date */}
          <div className="space-y-2">
            <Label>Scheduled Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-slate-50",
                    !date && "text-muted-foreground"
                  )}
                  data-testid="maintenance-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setFormData(prev => ({ 
                      ...prev, 
                      scheduled_date: d ? format(d, 'yyyy-MM-dd') : '' 
                    }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Estimated Cost and Hourly Rate */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="estimated_cost">Estimated Cost ($)</Label>
              <Input
                id="estimated_cost"
                type="number"
                min="0"
                value={formData.estimated_cost}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  estimated_cost: parseFloat(e.target.value) || 0 
                }))}
                className="bg-slate-50"
                data-testid="maintenance-cost-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
              <Input
                id="hourly_rate"
                type="number"
                min="0"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                placeholder="35.00"
                className="bg-slate-50"
                data-testid="maintenance-hourly-rate-input"
              />
            </div>
          </div>

          {/* Recurring Task */}
          <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-slate-600" />
                <Label htmlFor="is_recurring" className="font-medium">Recurring Task</Label>
              </div>
              <Switch
                id="is_recurring"
                checked={formData.is_recurring}
                onCheckedChange={(checked) => setFormData(prev => ({ 
                  ...prev, 
                  is_recurring: checked,
                  recurrence_pattern: checked ? 'monthly' : null
                }))}
                data-testid="maintenance-recurring-switch"
              />
            </div>
            
            {formData.is_recurring && (
              <div className="space-y-2 pt-2">
                <Label>Recurrence Pattern</Label>
                <Select
                  value={formData.recurrence_pattern || 'monthly'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, recurrence_pattern: value }))}
                >
                  <SelectTrigger data-testid="maintenance-recurrence-select">
                    <SelectValue placeholder="Select pattern" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_PATTERNS.map((pattern) => (
                      <SelectItem key={pattern.value} value={pattern.value}>
                        {pattern.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Task will automatically regenerate based on this schedule
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the issue..."
              className="bg-slate-50"
              rows={3}
              data-testid="maintenance-description-input"
            />
          </div>

          {/* Share With (collaborators) */}
          <ShareWithPicker
            value={formData.collaborator_ids}
            onChange={(ids) => setFormData((prev) => ({ ...prev, collaborator_ids: ids }))}
            excludeIds={[formData.assigned_to]}
            testIdPrefix="maintenance-share-with"
          />

          {/* Photos */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Photos (optional)
            </Label>
            <PhotoUploader
              kind="maintenance_photo"
              onAdd={addPhoto}
              testIdPrefix="maintenance-photo-uploader"
            />

            {/* Photo Preview Grid */}
            {formData.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {formData.photos.map((photo, index) => (
                  <div key={`photo-${photo.slice(-20)}-${index}`} className="relative group">
                    <img
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg border border-slate-200"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`maintenance-remove-photo-${index}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => { resetForm(); onClose(); }}
              data-testid="maintenance-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="maintenance-submit-btn"
            >
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceDialog;
